import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioStreams } from '../contexts/AudioStreamsContext';
import { usePermissions } from '../hooks/usePermissions';
import { srsService } from '../services/srsService';
import { SquawkPlayer } from './SquawkPlayer';

// Theme colors for iframe styling
const THEME = {
  bgColor: '0A0A0A',
  fontColor: '9ca3af',
  surfaceColor: '161616',
};

/**
 * Builds the iframe HTML content for a FinancialJuice widget.
 */
function buildIframeContent(widgetType: 'Voice' | 'TS', width: number, height: number): string {
  const containerId = `fj-drawer-widget-${widgetType.toLowerCase()}`;
  const scriptUrl = 'https://feed.financialjuice.com/widgets/widgets.js';

  const optionsJs = `
    options.container = "${containerId}";
    options.widgetType = "${widgetType}";
    options.width = "${width}px";
    options.height = "${height}px";
    options.mode = "inline";
  `;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
          width: 100%;
          height: 100%;
          background-color: #${THEME.bgColor};
          color: #${THEME.fontColor};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          overflow: hidden;
        }
        #${containerId} {
          width: 100%;
          height: 100%;
        }
        .fj-widget, .fj-voice-widget, .fj-ts-widget {
          background-color: #${THEME.bgColor} !important;
        }
      </style>
    </head>
    <body>
      <div id="${containerId}"></div>
      <script type="text/javascript">
        (function() {
          var jo = document.createElement("script");
          jo.type = "text/javascript";
          jo.id = "FJ-Widgets";
          jo.src = "${scriptUrl}";

          jo.onload = function() {
            if (window.FJWidgets && window.FJWidgets.createWidget) {
              try {
                var options = {};
                ${optionsJs}
                new window.FJWidgets.createWidget(options);
              } catch (error) {
                console.error('Audio drawer widget error:', error);
              }
            }
          };

          document.getElementsByTagName("head")[0].appendChild(jo);
        })();
      </script>
    </body>
    </html>
  `;
}

interface DrawerWidgetProps {
  widgetType: 'Voice' | 'TS';
  height: number;
}

function DrawerWidget({ widgetType, height }: DrawerWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [iframeKey] = useState(() => Date.now()); // Stable key for iframe persistence

  // Measure container dimensions
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const width = container.clientWidth;
      if (width > 0) {
        setDimensions({ width, height });
      }
    };

    updateDimensions();

    const observer = new ResizeObserver(updateDimensions);
    observer.observe(container);

    return () => observer.disconnect();
  }, [height]);

  return (
    <div
      ref={containerRef}
      className="bg-dark-bg border-b border-dark-border"
      style={{ height: `${height}px` }}
    >
      {dimensions && (
        <iframe
          key={iframeKey}
          title={widgetType === 'Voice' ? 'FJ Squawk' : 'TickStrike'}
          srcDoc={buildIframeContent(widgetType, dimensions.width, dimensions.height)}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin"
        />
      )}
    </div>
  );
}

/**
 * Slide-out drawer containing FJ Squawk and TickStrike widgets.
 * Persists across page navigation by staying mounted (hidden when closed).
 */
// Shared SRS SDK + adapter URLs (single SRS instance)
const SRS_SDK_URL = 'https://squawk.steamroomtrading.com/players/js/srs.sdk.js';
const SRS_ADAPTER_URL = 'https://squawk.steamroomtrading.com/players/js/adapter-7.4.0.min.js';

export function AudioDrawer() {
  const { drawerOpen, setDrawerOpen } = useAudioStreams();
  const { hasPermission } = usePermissions();

  const getSteamroomUrl = useCallback(async () => {
    const { url } = await srsService.getPlayToken('steamroom');
    return url;
  }, []);

  const getZerodayUrl = useCallback(async () => {
    const { url } = await srsService.getPlayToken('zeroday');
    return url;
  }, []);

  return (
    <>
      {/* Backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-dark-surface border-l border-dark-border z-50 transform transition-transform duration-300 ease-in-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-dark-border bg-dark-surface-light">
          <h2 className="text-sm font-semibold text-white">Audio Streams</h2>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-gray-400 hover:text-white hover:bg-dark-surface rounded p-1 transition-colors"
            title="Close drawer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Widgets */}
        <div className="flex flex-col">
          {hasPermission('steamroom') && (
            <SquawkPlayer
              // ⚠️ DISPLAY ONLY. The permission key, the stream URL and the
              // play-token channel below all stay 'steamroom' — they are
              // backend contracts, and renaming any of them silently kills the
              // feed rather than failing loudly.
              label="FZN PRO"
              streamUrl="webrtc://squawk.steamroomtrading.com/live/steamroom"
              sdkUrl={SRS_SDK_URL}
              adapterUrl={SRS_ADAPTER_URL}
              getStreamUrl={getSteamroomUrl}
            />
          )}
          {(hasPermission('steamroom') || hasPermission('zeroday')) && (
            <SquawkPlayer
              label="FZN ZERODAY"
              streamUrl="webrtc://squawk.steamroomtrading.com/live/zeroday"
              sdkUrl={SRS_SDK_URL}
              adapterUrl={SRS_ADAPTER_URL}
              getStreamUrl={getZerodayUrl}
            />
          )}
          <DrawerWidget widgetType="Voice" height={80} />
          <DrawerWidget widgetType="TS" height={180} />
        </div>
      </div>
    </>
  );
}
